import { Fragment, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Heart, Menu, ChevronDown } from "lucide-react";
import { logoSmallUrl, logoSrcSet } from "@/lib/images";
import { ISLANDS, ISLAND_PATH, islandUrl, complexesUrl, COMPLEXES_PATH, tipsUrl, TIPS_PATH } from "@/lib/translations";
import { useWishlist } from "@/context/WishlistContext";
import { useSiteSettings } from "@/context/LanguageContext";
import { LANG_LABELS, publicSiteLangs, tr } from "@/lib/translations";
import { SiteClaim } from "@/components/SiteClaim";
import { dv } from "@/lib/theme";
import { navVisible, navMainOrder } from "@/lib/navItems";
import { usePages } from "@/lib/shared";

// „Ferienobjekte“-Menü: alle Objekte, Ferienwohnungen, Ferienhäuser, Fincas & Anlagen
const PROPERTY_MENU = [
  { key: "all", to: "/properties", label: (t) => t.nav.allProperties },
  { key: "apartments", to: "/properties?type=apartment", label: (t) => t.nav.apartments },
  { key: "houses", to: "/properties?type=villa", label: (t) => t.nav.houses },
  { key: "complexes", to: complexesUrl(), label: (t) => t.nav.complexes },
];

const FLAG = { de: "de", en: "gb", es: "es", nl: "nl", pl: "pl", fr: "fr", it: "it" };
const isInternal = (url) => /^\//.test(url || "");

export default function Header() {
  const { lang, setLang, t, siteLoaded } = useLang();
  const { ids } = useWishlist();
  const [open, setOpen] = useState(false);
  const pages = usePages().filter((p) => p.in_menu);
  const site = useSiteSettings();
  const show = navVisible(site);
  const logo = site.site_logo_url || "";
  const logoSmall = logoSmallUrl(logo);
  const pathname = useLocation().pathname;
  const isIsland = Object.values(ISLAND_PATH).some((p) => pathname === `/${p}`);
  const isProperties = pathname === "/properties" || pathname === `/${COMPLEXES_PATH}`;
  const inPortal = pathname === "/vermieter" || pathname.startsWith("/vermieter/");  // Vermieter-Bereich: eigener Sprachumschalter, kein „Vermieter“-Link, Website-Links in neuem Tab
  const inCustomer = pathname === "/kunde" || pathname.startsWith("/kunde/");  // Kundenbereich: eigener Sprachumschalter, kein „Meine Buchungen“-Link
  const ext = inPortal || inCustomer ? { target: "_blank", rel: "noreferrer" } : {};
  const close = () => setOpen(false);
  const menu = navMainOrder(site, lang).filter((m) => show(m.key));

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-brand" : "text-stone-700 hover:text-brand"}`;
  const ddTrigger = (active) => `text-sm tracking-wide flex items-center gap-1 outline-none ${active ? "text-brand" : "text-stone-700 hover:text-brand"}`;

  // Eigener Menüpunkt (Admin → Design → Kopfzeile → Menüpunkte): interner Pfad oder externe Adresse
  const customLink = (c, cls, mobile = false) => {
    const label = tr(c.label, lang) || c.label?.de || c.url;
    const internal = isInternal(c.url);
    const props = { ...(c.new_tab || !internal ? { target: "_blank", rel: "noreferrer" } : ext), onClick: mobile ? close : undefined, "data-testid": `${mobile ? "mobile-" : ""}nav-custom-${c.id}` };
    return internal ? <NavLink to={c.url} {...props} className={cls}>{label}</NavLink> : <a href={c.url} {...props} className={typeof cls === "function" ? cls({ isActive: false }) : cls}>{label}</a>;
  };

  const desktopItem = ({ key, custom }) => {
    switch (key) {
      case "home": return <NavLink to="/" end {...ext} className={linkCls} data-testid="nav-home">{t.nav.home}</NavLink>;
      case "properties": return (
        <DropdownMenu>
          <DropdownMenuTrigger className={ddTrigger(isProperties)} data-testid="nav-properties">{t.nav.properties} <ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-white min-w-[230px]">
            {PROPERTY_MENU.map((m) => (
              <DropdownMenuItem key={m.key} asChild data-testid={`nav-properties-${m.key}`}>
                <Link to={m.to} {...ext} className={m.key === "all" ? "font-semibold" : ""}>{m.label(t)}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>);
      case "islands": return (
        <DropdownMenu>
          <DropdownMenuTrigger className={ddTrigger(isIsland)} data-testid="nav-islands">{t.nav.islands} <ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-white min-w-[220px]">
            {ISLANDS.map((i) => (
              <DropdownMenuItem key={i} asChild data-testid={`nav-island-${i}`}>
                <Link to={islandUrl(i)} {...ext} className="font-semibold">{t.islands[i]}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>);
      case "tips": return pages.length === 0 ? <NavLink to={tipsUrl()} {...ext} className={linkCls} data-testid="nav-pages">{t.nav.tips}</NavLink> : (
        <DropdownMenu>
          <DropdownMenuTrigger className={ddTrigger(pathname === `/${TIPS_PATH}`)} data-testid="nav-pages">{t.nav.tips} <ChevronDown className="w-3.5 h-3.5" /></DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-white max-h-[80vh] overflow-y-auto min-w-[220px]">
            <DropdownMenuItem asChild data-testid="nav-tips-all">
              <Link to={tipsUrl()} {...ext} className="font-semibold">{t.tips.all}</Link>
            </DropdownMenuItem>
            {pages.map((p) => (
              <DropdownMenuItem key={p.slug} asChild data-testid={`nav-page-${p.slug}`}>
                <Link to={p.path} {...ext}>{tr(p.title, lang)}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>);
      case "about": return <a href={inPortal ? "/#about" : "#about"} {...ext} className="text-sm text-stone-700 hover:text-brand whitespace-nowrap" data-testid="nav-about">{t.nav.about}</a>;
      case "contact": return <NavLink to="/kontakt" {...ext} className={linkCls} data-testid="nav-contact">{t.nav.contact}</NavLink>;
      case "deals": return (
        <Link to="/properties?deals=1" {...ext} className="text-sm tracking-wide text-signal hover:text-signal/80 font-semibold flex items-center gap-1.5" data-testid="nav-deals">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-signal/60 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-signal" /></span>
          {t.nav.deals}
        </Link>);
      default: return custom ? customLink(custom, linkCls) : null;
    }
  };

  const mobileItem = ({ key, custom }) => {
    switch (key) {
      case "home": return <NavLink to="/" {...ext} onClick={close} className="text-sm">{t.nav.home}</NavLink>;
      case "properties": return (<>
        <div className="text-[10px] uppercase tracking-widest text-brand mt-2">{t.nav.properties}</div>
        <div className="grid grid-cols-2 gap-2 pl-2">
          {PROPERTY_MENU.map((m) => <Link key={m.key} to={m.to} {...ext} onClick={close} className="text-sm font-semibold" data-testid={`mobile-nav-properties-${m.key}`}>{m.label(t)}</Link>)}
        </div>
      </>);
      case "islands": return (<>
        <div className="text-[10px] uppercase tracking-widest text-brand mt-2">{t.nav.islands}</div>
        <div className="grid grid-cols-2 gap-2 pl-2">
          {ISLANDS.map((i) => <Link key={i} to={islandUrl(i)} {...ext} onClick={close} className="text-sm font-semibold" data-testid={`mobile-nav-island-${i}`}>{t.islands[i]}</Link>)}
        </div>
      </>);
      case "tips": return (<>
        <Link to={tipsUrl()} {...ext} onClick={close} className="text-sm font-semibold" data-testid="mobile-nav-tips-all">{t.nav.tips}</Link>
        {pages.map((p) => <Link key={p.slug} to={p.path} {...ext} onClick={close} className="text-sm pl-3" data-testid={`mobile-nav-page-${p.slug}`}>{tr(p.title, lang)}</Link>)}
      </>);
      case "about": return <a href={inPortal ? "/#about" : "#about"} {...ext} onClick={close} className="text-sm" data-testid="mobile-nav-about">{t.nav.about}</a>;
      case "contact": return <Link to="/kontakt" {...ext} onClick={close} className="text-sm" data-testid="mobile-nav-contact">{t.nav.contact}</Link>;
      case "deals": return <Link to="/properties?deals=1" {...ext} onClick={close} className="text-sm text-signal font-semibold" data-testid="mobile-nav-deals">● {t.nav.deals}</Link>;
      default: return custom ? customLink(custom, "text-sm", true) : null;
    }
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between" style={{ height: dv(site, "header_height") }}>
        <Link to="/" {...ext} className="group flex items-center gap-3 relative" data-testid="nav-logo">
          {siteLoaded
            ? <img src={logoSmall || logo || "/logo.png"} srcSet={logoSrcSet(logo) || undefined} sizes={logoSmall ? "(max-width: 767px) 96px, 125px" : undefined} onError={(e) => { const el = e.currentTarget; el.removeAttribute("srcset"); el.removeAttribute("sizes"); if (logoSmall && el.src !== logo && !el.src.endsWith("/logo.png")) el.src = logo; else if (!el.src.endsWith("/logo.png")) el.src = "/logo.png"; }} alt="privateHOLIDAY" width="125" height="100" className="w-auto relative top-2 drop-shadow-md transition-transform duration-300 ease-out group-hover:-rotate-2 group-hover:scale-[1.03] motion-reduce:transform-none" style={{ height: dv(site, "logo_height") }} data-testid="site-logo-img" />
            : <span aria-hidden="true" className="inline-block relative top-2" style={{ height: dv(site, "logo_height"), width: "125px" }} data-testid="site-logo-placeholder" />}
          <SiteClaim s={site} className="hidden sm:block" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-10">
          {menu.map((m) => <Fragment key={m.key}>{desktopItem(m)}</Fragment>)}
        </nav>

        <div className="flex items-center gap-3">

          {show("wishlist") && <Link to="/merkliste" {...ext} className="relative" aria-label={`${t.footer?.wishlist || "Merkliste"}${ids.length ? ` (${ids.length})` : ""}`} data-testid="nav-wishlist">
            <Button variant="ghost" size="sm" className="gap-1.5" tabIndex={-1} aria-hidden="true"><Heart className={`w-4 h-4 ${ids.length ? "fill-signal text-signal" : ""}`} />
              {ids.length > 0 && <span className="text-xs font-semibold" data-testid="nav-wishlist-count">{ids.length}</span>}
            </Button>
          </Link>}
          {show("customer") && !inCustomer && <Link to="/kunde/login" {...ext} className="hidden lg:block">
            <Button variant="ghost" size="sm" data-testid="nav-customer">{t.nav.customer || "Meine Buchungen"}</Button>
          </Link>}
          {show("landlord") && !inPortal && <Link to="/vermieter/login" {...ext} className="hidden sm:block">
            <Button variant="ghost" size="sm" data-testid="nav-landlord">{t.nav.landlord}</Button>
          </Link>}

          {!inPortal && !inCustomer && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher-trigger">
                <img src={`https://flagcdn.com/w40/${FLAG[lang]}.png`} width="20" height="14" alt="" className="rounded-[2px] shadow-sm" />
                <span className="uppercase text-xs tracking-wider">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              {[...new Set([...publicSiteLangs, lang])].map((code) => (
                <DropdownMenuItem key={code} onClick={() => setLang(code)} className="gap-2"
                  data-testid={`language-switcher-${code}`}>
                  <img src={`https://flagcdn.com/w40/${FLAG[code]}.png`} width="20" height="14" alt="" className="rounded-[2px]" />
                  {LANG_LABELS[code]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>}

          <button className="xl:hidden" onClick={() => setOpen(!open)} aria-label={open ? "Menü schließen" : "Menü öffnen"} aria-expanded={open} data-testid="mobile-menu-toggle">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-stone-200 bg-white/95 px-6 py-4 flex flex-col gap-3">
          {menu.map((m) => <Fragment key={m.key}>{mobileItem(m)}</Fragment>)}
          {show("customer") && !inCustomer && <Link to="/kunde/login" {...ext} onClick={close} className="text-sm" data-testid="nav-customer-mobile">{t.nav.customer || "Meine Buchungen"}</Link>}
          {show("landlord") && !inPortal && <Link to="/vermieter/login" onClick={close} className="text-sm">{t.nav.landlord}</Link>}
        </div>
      )}
    </header>
  );
}
